import React, { useState, useEffect } from 'react';
import { Alert, Snackbar, Box, Chip } from '@mui/material';
import {
    WifiOff as WifiOffIcon,
    Wifi as WifiIcon,
    CloudOff as CloudOffIcon
} from '@mui/icons-material';
import OfflineStorageService from '../services/offlineStorageService';

const OfflineIndicator: React.FC = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [showNotification, setShowNotification] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

    useEffect(() => {
        const handleOnline = () => {
            console.log('✅ Device is online');
            setIsOnline(true);
            setShowNotification(true);
            OfflineStorageService.setOfflineMode(false);
        };

        const handleOffline = () => {
            console.log('📴 Device is offline');
            setIsOnline(false);
            setShowNotification(true);
            OfflineStorageService.setOfflineMode(true);
        };

        // Add event listeners
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Check last sync time
        setLastSyncTime(OfflineStorageService.getLastSyncTime());

        // Cleanup
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const handleCloseNotification = () => {
        setShowNotification(false);
    };

    const formatLastSync = () => {
        if (!lastSyncTime) return 'Never';
        
        const now = new Date();
        const diff = now.getTime() - lastSyncTime.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
        if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        return 'Just now';
    };

    return (
        <>
            {/* Persistent offline indicator */}
            {!isOnline && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        py: 1,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        zIndex: 9999,
                        boxShadow: 2
                    }}
                >
                    <WifiOffIcon fontSize="small" />
                    <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        You're offline - Using cached data
                    </Box>
                    {lastSyncTime && (
                        <Chip
                            label={`Last synced: ${formatLastSync()}`}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                color: 'inherit',
                                fontSize: '0.75rem'
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Notification snackbar */}
            <Snackbar
                open={showNotification}
                autoHideDuration={4000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={isOnline ? 'success' : 'warning'}
                    icon={isOnline ? <WifiIcon /> : <CloudOffIcon />}
                    sx={{ width: '100%' }}
                >
                    {isOnline
                        ? 'Back online! Syncing latest rates...'
                        : 'You\'re offline. Using cached exchange rates.'}
                </Alert>
            </Snackbar>
        </>
    );
};

export default OfflineIndicator;
